import React, { useState } from 'react';
import { IoArrowBack, IoStar, IoStarOutline } from 'react-icons/io5';
import './ChatPage.css';
import { usePov } from '../context/PovContext';

interface Message {
  sender: string;
  message: string;
  timestamp: string;
}

interface Conversation {
  id: string;
  photo?: string;
  catch_phrase: string;
  messages: Message[];
}

interface ChatPageProps {
  conversation: Conversation;
  onBack: () => void;
}

const ChatPage: React.FC<ChatPageProps> = ({ conversation, onBack }) => {
  const { pov } = usePov();
  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  // Group messages by date
  const groupMessagesByDate = () => {
    const groups: { [key: string]: Message[] } = {};
    
    conversation.messages.forEach(message => {
      const dateKey = formatDate(message.timestamp);
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(message);
    });
    
    return groups;
  };

  const messageGroups = groupMessagesByDate();

  const handleStarClick = (starRating: number) => {
    setRating(starRating);
  };

  const handleStarHover = (starRating: number) => {
    setHoveredRating(starRating);
  };

  const handleStarLeave = () => {
    setHoveredRating(0);
  };

  const renderStarRating = () => {
    return (
      <div className="star-rating">
        {[1, 2, 3, 4, 5].map((star) => {
          const isFilled = star <= (hoveredRating || rating);
          const StarIcon = isFilled ? IoStar : IoStarOutline;
          
          return (
            <button
              key={star}
              className="star-button"
              onClick={() => handleStarClick(star)}
              onMouseEnter={() => handleStarHover(star)}
              onMouseLeave={handleStarLeave}
            >
              {React.createElement(StarIcon as React.ComponentType<any>)}
            </button>
          );
        })}
      </div>
    );
  };

  // Find the first "them" message in the conversation
  const getFirstThemMessage = () => {
    for (const messages of Object.values(messageGroups)) {
      const firstThemMessage = messages.find(msg => msg.sender === 'them');
      if (firstThemMessage) {
        return firstThemMessage;
      }
    }
    return null;
  };

  const firstThemMessage = getFirstThemMessage();

  return (
    <div className="page-content">
      <div className="chat-container">
        <div className="chat-header">
          <button className="back-button" onClick={onBack}>
            {React.createElement(IoArrowBack as React.ComponentType<any>)}
          </button>
          <div className="chat-profile">
            {conversation.photo ? (
              <img src={conversation.photo} alt="Profile" className="chat-avatar" />
            ) : (
              <div className="chat-avatar-placeholder" />
            )}
            <div className="chat-info">
              <div className="chat-phrase">{conversation.catch_phrase}</div>
            </div>
          </div>
        </div>

        <div className="messages-container">
          {Object.entries(messageGroups).map(([date, messages]) => (
            <div key={date} className="message-group">
              <div className="date-separator">
                <span>{date}</span>
              </div>
              
              {messages.map((message, index) => {
                const isFirstThemMessage = message === firstThemMessage;
                return (
                  <div
                    key={index}
                    className={`message ${message.sender === 'me' ? 'message-sent' : 'message-received'}`}
                  >
                    <div className="message-bubble">
                      <p>{message.message}</p>
                      <div className="message-footer">
                        <span className="message-time">{formatTime(message.timestamp)}</span>
                        {isFirstThemMessage && renderStarRating()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className="chat-input-container">
          <div className="chat-input-placeholder">
            <span>This is a demo - messages are read-only</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
